import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";

/**
 * Siri-like animated voice orb with waveform
 * States: idle, listening, speaking
 */
export default function VoiceOrb({ state = "idle", amplitude = 0.5 }) {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const phaseRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;

    // Set canvas size
    const resize = () => {
      const size = canvas.offsetWidth;
      canvas.width = size * dpr;
      canvas.height = size * dpr;
      ctx.scale(dpr, dpr);
    };
    resize();
    window.addEventListener("resize", resize);

    const centerX = canvas.offsetWidth / 2;
    const centerY = canvas.offsetHeight / 2;
    const baseRadius = canvas.offsetWidth * 0.3;

    const draw = () => {
      ctx.clearRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);

      phaseRef.current += 0.05;

      // Gradient background glow
      const gradient = ctx.createRadialGradient(
        centerX, centerY, 0,
        centerX, centerY, baseRadius * 2
      );

      if (state === "speaking") {
        gradient.addColorStop(0, "rgba(201, 168, 106, 0.3)");
        gradient.addColorStop(0.5, "rgba(201, 168, 106, 0.1)");
        gradient.addColorStop(1, "rgba(201, 168, 106, 0)");
      } else if (state === "listening") {
        gradient.addColorStop(0, "rgba(11, 32, 39, 0.5)");
        gradient.addColorStop(0.5, "rgba(11, 32, 39, 0.2)");
        gradient.addColorStop(1, "rgba(11, 32, 39, 0)");
      } else {
        gradient.addColorStop(0, "rgba(201, 168, 106, 0.15)");
        gradient.addColorStop(1, "rgba(201, 168, 106, 0)");
      }

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);

      // Draw waveform circle
      ctx.beginPath();
      const points = state === "idle" ? 60 : 120;
      const amp = state === "speaking" ? amplitude * 40 : state === "listening" ? 20 : 5;

      for (let i = 0; i <= points; i++) {
        const angle = (i / points) * Math.PI * 2;
        const wave1 = Math.sin(phaseRef.current + angle * 3) * amp * 0.5;
        const wave2 = Math.sin(phaseRef.current * 1.3 + angle * 5) * amp * 0.3;
        const wave3 = Math.sin(phaseRef.current * 0.7 + angle * 7) * amp * 0.2;
        const radius = baseRadius + wave1 + wave2 + wave3;

        const x = centerX + Math.cos(angle) * radius;
        const y = centerY + Math.sin(angle) * radius;

        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }

      ctx.closePath();

      // Fill with gradient
      const fillGradient = ctx.createRadialGradient(
        centerX, centerY, baseRadius * 0.3,
        centerX, centerY, baseRadius
      );

      if (state === "speaking") {
        fillGradient.addColorStop(0, "rgba(201, 168, 106, 0.9)");
        fillGradient.addColorStop(1, "rgba(201, 168, 106, 0.3)");
      } else if (state === "listening") {
        fillGradient.addColorStop(0, "rgba(11, 32, 39, 0.9)");
        fillGradient.addColorStop(1, "rgba(11, 32, 39, 0.5)");
      } else {
        fillGradient.addColorStop(0, "rgba(201, 168, 106, 0.6)");
        fillGradient.addColorStop(1, "rgba(201, 168, 106, 0.2)");
      }

      ctx.fillStyle = fillGradient;
      ctx.fill();

      // Stroke
      ctx.strokeStyle = state === "speaking" ? "rgba(201, 168, 106, 0.8)" :
                        state === "listening" ? "rgba(11, 32, 39, 0.8)" :
                        "rgba(201, 168, 106, 0.4)";
      ctx.lineWidth = 2;
      ctx.stroke();

      // Inner circle for logo
      ctx.beginPath();
      ctx.arc(centerX, centerY, baseRadius * 0.4, 0, Math.PI * 2);
      ctx.fillStyle = state === "speaking" ? "rgba(11, 32, 39, 0.9)" :
                      state === "listening" ? "rgba(201, 168, 106, 0.9)" :
                      "rgba(11, 32, 39, 0.8)";
      ctx.fill();
      ctx.strokeStyle = state === "speaking" ? "rgba(201, 168, 106, 0.6)" :
                        state === "listening" ? "rgba(11, 32, 39, 0.6)" :
                        "rgba(201, 168, 106, 0.5)";
      ctx.lineWidth = 1;
      ctx.stroke();

      animationRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animationRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [state, amplitude]);

  return (
    <div className="relative w-full h-full">
      <canvas
        ref={canvasRef}
        className="w-full h-full"
        style={{ width: "100%", height: "100%" }}
      />
      {/* Sofia S monogram overlay */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <motion.span
          className="font-display italic text-6xl"
          style={{
            color: state === "speaking" ? "#C9A86A" :
                   state === "listening" ? "#0B2027" : "#C9A86A",
            letterSpacing: "-0.04em",
            fontWeight: 500,
          }}
          animate={{
            scale: state === "speaking" ? [1, 1.1, 1] : state === "listening" ? [1, 0.95, 1] : 1,
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          S
        </motion.span>
      </div>
    </div>
  );
}
